import { DateTime } from "luxon";

import { findCpvCode } from "components/form_components/dropdown_list_selects/cpv_code_selects/cpv_code_utils";
import { exportDataToCsv } from "lib/exportArrayToCsv";
import { SignalSettings } from "lib/hooks/api/teams/useSignalSettings";
import { normaliseKeywords } from "lib/utils/signalSettingsUtils";

export type SignalSettingsExportRow = {
  "Signal type": string;
  Signal: string;
  Description: string;
};

type CpvCodeName = { code: string; name: string };

const byText = (a: string, b: string) => a.localeCompare(b);

// Spreadsheets run cells starting with these as formulas; a leading apostrophe forces text
const FORMULA_PREFIX = /^[=+\-@\t\r]/;
const escapeFormula = (value: string) => (FORMULA_PREFIX.test(value) ? `'${value}` : value);

// Spreadsheets open bare digits as numbers, dropping leading zeros (03000000 shows as 3000000).
// A ="…" cell keeps them as text; only all-digit codes are wrapped, so nothing user-entered runs.
const asTextCell = (digits: string) =>
  /^\d+$/.test(digits) ? `="${digits}"` : escapeFormula(digits);

/**
 * One row per saved signal, in the same order as the signal settings page, so the
 * file can be filtered by type in a spreadsheet.
 */
export function buildSignalSettingsExportRows(
  signals: SignalSettings,
  allCpvCodes: CpvCodeName[] = [],
): SignalSettingsExportRow[] {
  const row = (type: string, signal: string, description = ""): SignalSettingsExportRow => ({
    "Signal type": type,
    Signal: escapeFormula(signal),
    Description: escapeFormula(description),
  });

  return [
    ...(signals.countries ?? [])
      .slice()
      .sort(byText)
      .map((c) => row("Country", c)),
    ...(signals.languages ?? [])
      .slice()
      .sort(byText)
      .map((l) => row("Language", l)),
    ...normaliseKeywords(signals.keywords).map((k) => row("Keyword", k)),
    ...signals.competitors
      .map((s) => s.name)
      .sort(byText)
      .map((name) => row("Competitor", name)),
    ...signals.partners
      .map((s) => s.name)
      .sort(byText)
      .map((name) => row("Partner", name)),
    ...(signals.cpvCodes ?? [])
      .slice()
      .sort(byText)
      .map((code) => ({
        ...row("CPV code", code, findCpvCode(allCpvCodes, code)?.name ?? ""),
        Signal: asTextCell(code),
      })),
  ];
}

export function exportSignalSettingsToCsv(rows: SignalSettingsExportRow[]): void {
  const date = DateTime.now().toISODate();
  exportDataToCsv(`signal-settings-${date}.csv`, rows, true, true);
}
